import {
  Table,
  Header,
  Pagination,
  SpaceBetween,
  Button,
  StatusIndicator,
  Box,
  Link,
  Select,
  FormField,
  Input,
  CollectionPreferences,
  CopyToClipboard,
  Popover,
} from '@cloudscape-design/components';
import { useCollection } from '@cloudscape-design/collection-hooks';
import { useNavigate } from 'react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEvaluations } from '../../hooks/useEvaluations';
import { getStatusType, formatSubject, EVALUATION_STATUS_DISPLAY, EVALUATION_STATUS_OPTIONS } from '../../utils/evaluationHelpers';
import { formatElapsedTime } from '../../utils/formatFieldName';
import { useTablePreferences } from '../../hooks/useTablePreferences';
import { useUrlSearchState } from '../../hooks/useUrlSearchState';
import type { EvaluationSummary } from '@amzn/helianthus-arbiter-client';
import { EvaluationStatus } from '@amzn/helianthus-arbiter-client';
import { useArbiterClient } from '../../context/ArbiterClientContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function EvaluationsListPage() {
  useDocumentTitle('Evaluations');
  const navigate = useNavigate();
  const client = useArbiterClient();

  const urlState = useUrlSearchState();

  // URL-backed filter state (replaces useState)
  const statusFilter = urlState.get('status');
  const [localSearchFilter, setLocalSearchFilter] = useState(urlState.get('bindle'));

  // Commit search: UUID → navigate to eval detail, otherwise → bindle filter
  const commitSearch = useCallback((text: string) => {
    const trimmed = text.trim();
    if (UUID_RE.test(trimmed)) {
      navigate(`/evaluations/${encodeURIComponent(trimmed)}`);
    } else {
      urlState.set('bindle', trimmed);
    }
  }, [urlState, navigate]);

  // Read sort state from URL (D-09)
  const sortField = urlState.get('sort');
  const sortDir = urlState.get('dir');
  const [selectedItems, setSelectedItems] = useState<EvaluationSummary[]>([]);
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set());

  const committedBindleFilter = urlState.get('bindle');

  const { evaluations, loading, fetchingMore, error, refetch } = useEvaluations({
    completed: statusFilter ? (statusFilter as EvaluationStatus) : undefined,
    bindleLock: committedBindleFilter || undefined,
    limit: 50,
  });

  const {
    pageSize,
    columnDisplay,
    getColumnWidth,
    handleColumnWidthsChange,
    collectionPreferencesProps,
  } = useTablePreferences({
    storageKey: 'evaluations-table',
    defaults: {
      pageSize: 50,
      visibleContent: ['evaluationId', 'status', 'outcome', 'subject', 'bindleLock', 'startTime', 'endTime', 'elapsedTime'],
    },
    pageSizeOptions: [
      { value: 10, label: '10 evaluations' },
      { value: 20, label: '20 evaluations' },
      { value: 50, label: '50 evaluations' },
      { value: 100, label: '100 evaluations' },
    ],
    visibleContentOptions: [
      {
        label: 'Columns',
        options: [
          { id: 'evaluationId', label: 'Evaluation ID', editable: false },
          { id: 'status', label: 'Status' },
          { id: 'outcome', label: 'Outcome' },
          { id: 'subject', label: 'Subject' },
          { id: 'bindleLock', label: 'Bindle lock' },
          { id: 'startTime', label: 'Started' },
          { id: 'endTime', label: 'Completed' },
          { id: 'elapsedTime', label: 'Elapsed time' },
        ],
      },
    ],
  });

  // Column definitions extracted for sort column lookup (D-09, D-10 pattern, TABL-01)
  const columnDefinitions = useMemo(() => [
    {
      id: 'evaluationId',
      header: 'Evaluation ID',
      cell: (item: EvaluationSummary) => (
        <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
          <Link
            href={`/evaluations/${item.evaluationId}`}
            onFollow={(event) => {
              event.preventDefault();
              navigate(`/evaluations/${item.evaluationId}`);
            }}
            fontSize="body-s"
          >
            {item.evaluationId!.substring(0, 8)}
          </Link>
          <CopyToClipboard
            variant="icon"
            textToCopy={item.evaluationId!}
            copySuccessText="Evaluation ID copied"
            copyErrorText="Failed to copy"
            copyButtonAriaLabel={`Copy evaluation ID ${item.evaluationId}`}
          />
        </SpaceBetween>
      ),
      sortingField: 'evaluationId',
      width: getColumnWidth('evaluationId', 160),
      minWidth: 150,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item: EvaluationSummary) => (
        <StatusIndicator type={getStatusType(item.status!)}>
          {EVALUATION_STATUS_DISPLAY[item.status!]}
        </StatusIndicator>
      ),
      sortingField: 'status',
      width: getColumnWidth('status', 140),
      minWidth: 100,
    },
    {
      id: 'outcome',
      header: 'Result',
      cell: (item: EvaluationSummary) => {
        if (item.outcome === 'PASS') {
          return <StatusIndicator type="success">Pass</StatusIndicator>;
        }
        if (item.outcome === 'FAIL') {
          const failIndicator = <StatusIndicator type="error">Fail</StatusIndicator>;
          if (item.description) {
            return (
              <Popover
                header="Failure reason"
                content={item.description}
                dismissButton={false}
                position="top"
                size="medium"
                triggerType="custom"
              >
                {failIndicator}
              </Popover>
            );
          }
          return failIndicator;
        }
        return <Box color="text-status-inactive">—</Box>;
      },
      sortingComparator: (a: EvaluationSummary, b: EvaluationSummary) => {
        const outcomeOrder = (o?: string): number => o === 'PASS' ? 1 : o === 'FAIL' ? -1 : 0;
        return outcomeOrder(a.outcome) - outcomeOrder(b.outcome);
      },
      width: getColumnWidth('outcome', 130),
      minWidth: 100,
    },
    {
      id: 'subject',
      header: 'Subject',
      cell: (item: EvaluationSummary) => formatSubject(item),
      sortingComparator: (a: EvaluationSummary, b: EvaluationSummary) => {
        return formatSubject(a).localeCompare(formatSubject(b));
      },
      width: getColumnWidth('subject', 250),
      minWidth: 150,
    },
    {
      id: 'bindleLock',
      header: 'Bindle lock',
      cell: (item: EvaluationSummary) => item.bindleLock,
      sortingField: 'bindleLock',
      width: getColumnWidth('bindleLock', 160),
      minWidth: 100,
    },
    {
      id: 'startTime',
      header: 'Started',
      cell: (item: EvaluationSummary) => item.startTime?.toLocaleString() ?? '--',
      sortingField: 'startTime',
      width: getColumnWidth('startTime', 190),
      minWidth: 150,
    },
    {
      id: 'endTime',
      header: 'Completed',
      cell: (item: EvaluationSummary) =>
        item.endTime
          ? item.endTime.toLocaleString()
          : <Box color="text-status-inactive">—</Box>,
      sortingField: 'endTime',
      width: getColumnWidth('endTime', 190),
      minWidth: 150,
    },
    {
      id: 'elapsedTime',
      header: 'Elapsed time',
      cell: (item: EvaluationSummary) => formatElapsedTime(item.executionTime),
      sortingComparator: (a: EvaluationSummary, b: EvaluationSummary) => {
        return (a.executionTime ?? 0) - (b.executionTime ?? 0);
      },
      width: getColumnWidth('elapsedTime', 130),
      minWidth: 100,
    },
  ], [navigate, getColumnWidth]);

  // Look up sorting column from URL param (D-09, Pitfall 5)
  // Default to startTime descending (most recent first) when no URL sort param
  const startTimeColumn = useMemo(
    () => columnDefinitions.find((col) => col.sortingField === 'startTime'),
    [columnDefinitions],
  );
  const sortingColumn = useMemo(() => {
    if (!sortField) return startTimeColumn;
    return columnDefinitions.find(
      (col) => col.sortingField === sortField || col.id === sortField
    );
  }, [sortField, columnDefinitions, startTimeColumn]);

  const isDescending = sortField ? sortDir === 'desc' : true; // default descending for startTime

  const { items, filteredItemsCount, actions, collectionProps, paginationProps } = useCollection(
    evaluations,
    {
      pagination: { pageSize },
      sorting: {
        defaultState: sortingColumn
          ? { sortingColumn, isDescending }
          : undefined,
      },
    },
  );

  // Sync back-navigation sort state (critical for SRCH-02)
  useEffect(() => {
    if (sortingColumn) {
      actions.setSorting({ sortingColumn, isDescending });
    }
  }, [sortingColumn, isDescending, actions]);

  // Handle sort clicks -> URL (D-09)
  const handleSortingChange = useCallback(
    (event: { detail: { sortingColumn: { sortingField?: string; id?: string }; isDescending?: boolean } }) => {
      const field = event.detail.sortingColumn.sortingField ?? event.detail.sortingColumn.id ?? '';
      const dir = event.detail.isDescending ? 'desc' : 'asc';
      urlState.setMany({ sort: field, dir });
    },
    [urlState],
  );

  const handleCancel = async (evaluationId: string) => {
    setCancellingIds(prev => new Set(prev).add(evaluationId));
    try {
      await client.cancelEvaluation({ evaluationId });
      refetch();
    } catch (err) {
      console.error('Failed to cancel evaluation:', err);
    } finally {
      setCancellingIds(prev => {
        const updated = new Set(prev);
        updated.delete(evaluationId);
        return updated;
      });
    }
  };

  const canCancel = selectedItems.length === 1 && selectedItems[0].status === EvaluationStatus.IN_PROGRESS;

  return (
    <Table
      {...collectionProps}
      onSortingChange={handleSortingChange}
      columnDefinitions={columnDefinitions}
      items={items}
      loading={loading}
      loadingText="Loading evaluations..."
      selectionType="single"
      selectedItems={selectedItems}
      onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
      header={
        <Header
          variant="h1"
          counter={
            loading
              ? undefined
              : `(${filteredItemsCount ?? evaluations.length}${fetchingMore ? '+' : ''})`
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {canCancel && (
                <Button
                  disabled={cancellingIds.has(selectedItems[0]?.evaluationId ?? '')}
                  onClick={() => {
                    if (selectedItems[0]) {
                      handleCancel(selectedItems[0].evaluationId!);
                    }
                  }}
                  loading={cancellingIds.has(selectedItems[0].evaluationId ?? '')}
                >
                  Cancel Evaluation
                </Button>
              )}
            </SpaceBetween>
          }
        >
          Evaluations
        </Header>
      }
      filter={
        <SpaceBetween direction="horizontal" size="l">
          <FormField label="Status">
            <Select
              selectedOption={EVALUATION_STATUS_OPTIONS.find(opt => opt.value === statusFilter) || EVALUATION_STATUS_OPTIONS[0]}
              onChange={({ detail }) => {
                urlState.set('status', detail.selectedOption.value || '');
              }}
              options={EVALUATION_STATUS_OPTIONS}
              placeholder="Filter by status"
            />
          </FormField>
          <FormField label="Search">
            <div
              onKeyDown={(e) => { if (e.key === 'Enter') commitSearch(localSearchFilter); }}
              onBlur={() => {
                const trimmed = localSearchFilter.trim();
                if (!UUID_RE.test(trimmed)) commitSearch(trimmed);
              }}
            >
              <Input
                value={localSearchFilter}
                onChange={({ detail }) => setLocalSearchFilter(detail.value)}
                placeholder="Evaluation ID or bindle lock"
                type="search"
              />
            </div>
          </FormField>
        </SpaceBetween>
      }
      pagination={<Pagination {...paginationProps} />}
      resizableColumns
      columnDisplay={columnDisplay}
      onColumnWidthsChange={(event) =>
        handleColumnWidthsChange(
          columnDisplay.filter((c) => c.visible).map((c) => c.id),
          event,
        )
      }
      preferences={
        <CollectionPreferences
          {...collectionPreferencesProps}
          title="Preferences"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          <SpaceBetween size="m">
            <b>No evaluations</b>
            <Box variant="p" color="inherit">
              {error ? error : 'No evaluations match the current filters.'}
            </Box>
          </SpaceBetween>
        </Box>
      }
    />
  );
}
